import React, { useState, useEffect } from 'react';
import { 
  FileText, Video, Download, Upload, Trash2, Edit2, Save, X, 
  Loader2, Youtube, Link2, ExternalLink, CheckCircle, AlertCircle,
  Globe, Languages
} from 'lucide-react';
import { toast } from 'sonner';
import { documentsAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminCompetitionDocs = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDoc, setEditingDoc] = useState(null);
  const [isCreating, setIsCreating] = useState(null); // 'technical_report' or 'proof_of_flight'
  const [uploading, setUploading] = useState(false);
  const { language } = useLanguage();

  const [formData, setFormData] = useState({
    name: '',
    name_tr: '',
    description: '',
    description_tr: '',
    youtube_url: '',
    file: null
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const data = await documentsAPI.getAll();
      setDocuments(data || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      toast.error(language === 'tr' ? 'Dökümanlar yüklenemedi' : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const technicalReport = documents.find(d => d.category === 'technical_report');
  const proofOfFlight = documents.find(d => d.category === 'proof_of_flight');

  const handleCreate = async (category) => {
    if (!formData.name) {
      toast.error(language === 'tr' ? 'İsim zorunludur' : 'Name is required');
      return;
    }

    if (category === 'proof_of_flight' && !formData.youtube_url) {
      toast.error(language === 'tr' ? 'YouTube linki zorunludur' : 'YouTube URL is required');
      return;
    }

    if (category === 'technical_report' && !formData.file && !formData.youtube_url) {
      toast.error(language === 'tr' ? 'Dosya veya YouTube linki zorunludur' : 'File or YouTube URL is required');
      return;
    }

    setUploading(true);
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('name_tr', formData.name_tr || '');
      submitData.append('description', formData.description || '');
      submitData.append('description_tr', formData.description_tr || '');
      submitData.append('category', category);
      
      if (formData.youtube_url) {
        submitData.append('youtube_url', formData.youtube_url);
      }
      
      if (formData.file) {
        submitData.append('file', formData.file);
      }

      const response = await fetch(`${API_BASE_URL}/api/documents`, {
        method: 'POST',
        body: submitData
      });

      if (!response.ok) throw new Error('Failed to create document');

      toast.success(language === 'tr' ? 'Döküman oluşturuldu' : 'Document created');
      setIsCreating(null);
      resetForm();
      fetchDocuments();
    } catch (err) {
      toast.error(err.message || (language === 'tr' ? 'Döküman oluşturulamadı' : 'Failed to create document'));
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async (docId) => {
    setUploading(true);
    try {
      // Update metadata
      await documentsAPI.update(docId, {
        name: formData.name,
        name_tr: formData.name_tr,
        description: formData.description,
        description_tr: formData.description_tr,
        youtube_url: formData.youtube_url || null
      });

      // Update file if provided
      if (formData.file) {
        const fileData = new FormData();
        fileData.append('file', formData.file);
        
        await fetch(`${API_BASE_URL}/api/documents/${docId}/file`, {
          method: 'PUT',
          body: fileData
        });
      }

      toast.success(language === 'tr' ? 'Döküman güncellendi' : 'Document updated');
      setEditingDoc(null);
      resetForm();
      fetchDocuments();
    } catch (err) {
      toast.error(err.message || (language === 'tr' ? 'Döküman güncellenemedi' : 'Failed to update document'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm(language === 'tr' 
      ? 'Bu dökümanı silmek istediğinizden emin misiniz?' 
      : 'Are you sure you want to delete this document?'
    )) return;

    try {
      await documentsAPI.delete(docId);
      toast.success(language === 'tr' ? 'Döküman silindi' : 'Document deleted');
      fetchDocuments();
    } catch (err) {
      toast.error(language === 'tr' ? 'Döküman silinemedi' : 'Failed to delete document');
    }
  };

  const startEdit = (doc) => {
    setEditingDoc(doc.id);
    setFormData({
      name: doc.name || '',
      name_tr: doc.name_tr || '',
      description: doc.description || '',
      description_tr: doc.description_tr || '',
      youtube_url: doc.youtube_url || '',
      file: null
    });
    setIsCreating(null);
  };

  const startCreate = (category) => {
    setIsCreating(category);
    setEditingDoc(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      name_tr: '',
      description: '',
      description_tr: '',
      youtube_url: '',
      file: null
    });
  };

  const cancelEdit = () => {
    setEditingDoc(null);
    setIsCreating(null);
    resetForm();
  };

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]{11})/);
    return match ? match[1] : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
          <FileText size={28} className="text-cyan-400" />
          {language === 'tr' ? 'Yarışma Dökümanları Yönetimi' : 'Competition Documents Management'}
        </h1>
        <p className="text-gray-400">
          {language === 'tr' 
            ? 'Teknik Tasarım Raporu ve Uçuş Kanıtı dökümanlarını yönetin' 
            : 'Manage Technical Design Report and Proof of Flight documents'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Technical Design Report */}
        <DocumentCard
          title={language === 'tr' ? 'Teknik Tasarım Raporu' : 'Technical Design Report'}
          subtitle={language === 'tr' ? 'PDF veya döküman dosyası' : 'PDF or document file'}
          icon={<Download size={32} className="text-cyan-400" />}
          category="technical_report"
          document={technicalReport}
          editingDoc={editingDoc}
          isCreating={isCreating}
          formData={formData}
          setFormData={setFormData}
          uploading={uploading}
          language={language}
          onStartCreate={startCreate}
          onStartEdit={startEdit}
          onUpdate={handleUpdate}
          onCreate={handleCreate}
          onDelete={handleDelete}
          onCancel={cancelEdit}
          extractYouTubeId={extractYouTubeId}
          showFileUpload={true}
        />

        {/* Proof of Flight */}
        <DocumentCard
          title={language === 'tr' ? 'Uçuş Kanıtı' : 'Proof of Flight'}
          subtitle={language === 'tr' ? 'YouTube video linki' : 'YouTube video link'}
          icon={<Video size={32} className="text-cyan-400" />}
          category="proof_of_flight"
          document={proofOfFlight}
          editingDoc={editingDoc}
          isCreating={isCreating}
          formData={formData}
          setFormData={setFormData}
          uploading={uploading}
          language={language}
          onStartCreate={startCreate}
          onStartEdit={startEdit}
          onUpdate={handleUpdate}
          onCreate={handleCreate}
          onDelete={handleDelete}
          onCancel={cancelEdit}
          extractYouTubeId={extractYouTubeId}
          showFileUpload={false}
        />
      </div>

      {/* Info Note */}
      <div className="mt-8 p-4 rounded-xl" style={{ background: 'rgba(0, 240, 255, 0.05)', border: '1px solid rgba(0, 240, 255, 0.2)' }}>
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-cyan-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-cyan-300 font-medium mb-1">
              {language === 'tr' ? 'Bilgi' : 'Note'}
            </p>
            <p className="text-sm text-gray-400">
              {language === 'tr' 
                ? 'Bu dökümanlar ana sayfadaki "Yarışma Dökümanları" bölümünde ve ziyaretçilerin indirebileceği şekilde görüntülenir.' 
                : 'These documents are displayed in the "Competition Documents" section on the homepage and available for visitors to download.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Document Card Component
const DocumentCard = ({
  title,
  subtitle,
  icon,
  category,
  document,
  editingDoc,
  isCreating,
  formData,
  setFormData,
  uploading,
  language,
  onStartCreate,
  onStartEdit,
  onUpdate,
  onCreate,
  onDelete,
  onCancel,
  extractYouTubeId,
  showFileUpload
}) => {
  const isEditing = editingDoc === document?.id;
  const isCreatingThis = isCreating === category;
  const youtubeId = extractYouTubeId(document?.youtube_url || formData.youtube_url);

  return (
    <div 
      className="rounded-2xl p-6 transition-all duration-300"
      style={{ 
        background: 'rgba(30, 41, 59, 0.6)',
        border: document ? '1px solid rgba(0, 240, 255, 0.3)' : '1px solid rgba(148, 163, 184, 0.2)',
        backdropFilter: 'blur(16px)'
      }}
    >
      {/* Card Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-4">
          <div 
            className="w-16 h-16 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(0, 240, 255, 0.1)' }}
          >
            {icon}
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{title}</h2>
            <p className="text-sm text-gray-400">{subtitle}</p>
          </div>
        </div>
        {document && !isEditing && !isCreatingThis && (
          <CheckCircle size={24} className="text-green-400" />
        )}
      </div>

      {/* View Mode */}
      {document && !isEditing && !isCreatingThis && (
        <div className="space-y-4">
          {/* Preview */}
          {document.youtube_url && youtubeId && (
            <div className="rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <img 
                src={`https://img.youtube.com/vi/${extractYouTubeId(document.youtube_url)}/maxresdefault.jpg`}
                alt="Video thumbnail"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Document Info */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-cyan-400" />
              <span className="text-sm text-gray-400">EN:</span>
              <span className="text-white font-medium">{document.name}</span>
            </div>
            {document.name_tr && (
              <div className="flex items-center gap-2">
                <Languages size={16} className="text-cyan-400" />
                <span className="text-sm text-gray-400">TR:</span>
                <span className="text-white font-medium">{document.name_tr}</span>
              </div>
            )}
            {document.youtube_url && (
              <div className="flex items-center gap-2">
                <Youtube size={16} className="text-red-400" />
                <a 
                  href={document.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:underline text-sm truncate"
                >
                  {document.youtube_url}
                </a>
              </div>
            )}
            {document.filename && (
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-cyan-400" />
                <span className="text-gray-400 text-sm">{document.filename}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={() => onStartEdit(document)}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all"
              style={{ background: 'rgba(0, 240, 255, 0.1)', color: '#00F0FF', border: '1px solid rgba(0, 240, 255, 0.3)' }}
            >
              <Edit2 size={18} />
              {language === 'tr' ? 'Düzenle' : 'Edit'}
            </button>
            <button
              onClick={() => onDelete(document.id)}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all"
              style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.3)' }}
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {(isEditing || isCreatingThis) && (
        <div className="space-y-4">
          {/* Name EN */}
          <div>
            <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
              <Globe size={14} />
              {language === 'tr' ? 'İsim (İngilizce)' : 'Name (English)'} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={language === 'tr' ? 'Döküman ismi (EN)' : 'Document name (EN)'}
              className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-600 text-white focus:border-cyan-500 focus:outline-none transition-colors"
            />
          </div>

          {/* Name TR */}
          <div>
            <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
              <Languages size={14} />
              {language === 'tr' ? 'İsim (Türkçe)' : 'Name (Turkish)'}
            </label>
            <input
              type="text"
              value={formData.name_tr}
              onChange={(e) => setFormData({ ...formData, name_tr: e.target.value })}
              placeholder={language === 'tr' ? 'Döküman ismi (TR)' : 'Document name (TR)'}
              className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-600 text-white focus:border-cyan-500 focus:outline-none transition-colors"
            />
          </div>

          {/* Description EN */}
          <div>
            <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
              <Globe size={14} />
              {language === 'tr' ? 'Açıklama (İngilizce)' : 'Description (English)'}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={language === 'tr' ? 'Kısa açıklama (EN)' : 'Short description (EN)'}
              rows={2}
              className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-600 text-white focus:border-cyan-500 focus:outline-none transition-colors resize-none"
            />
          </div>

          {/* Description TR */}
          <div>
            <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
              <Languages size={14} />
              {language === 'tr' ? 'Açıklama (Türkçe)' : 'Description (Turkish)'}
            </label>
            <textarea
              value={formData.description_tr}
              onChange={(e) => setFormData({ ...formData, description_tr: e.target.value })}
              placeholder={language === 'tr' ? 'Kısa açıklama (TR)' : 'Short description (TR)'}
              rows={2}
              className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-600 text-white focus:border-cyan-500 focus:outline-none transition-colors resize-none"
            />
          </div>

          {/* YouTube URL */}
          <div>
            <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
              <Youtube size={14} className="text-red-400" />
              YouTube URL {!showFileUpload && '*'}
            </label>
            <input
              type="url"
              value={formData.youtube_url}
              onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-600 text-white focus:border-cyan-500 focus:outline-none transition-colors"
            />
            {formData.youtube_url && extractYouTubeId(formData.youtube_url) && (
              <div className="mt-3 rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                <img 
                  src={`https://img.youtube.com/vi/${extractYouTubeId(formData.youtube_url)}/maxresdefault.jpg`}
                  alt="Video preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          {/* File Upload */}
          {showFileUpload && (
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                <Upload size={14} />
                {language === 'tr' ? 'Dosya Yükle' : 'Upload File'}
              </label>
              <div 
                className="relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors hover:border-cyan-500"
                style={{ borderColor: 'rgba(148, 163, 184, 0.3)' }}
              >
                <input
                  type="file"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files?.[0] || null })}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept=".pdf,.doc,.docx,.ppt,.pptx"
                />
                {formData.file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText size={24} className="text-cyan-400" />
                    <span className="text-white">{formData.file.name}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setFormData({ ...formData, file: null }); }}
                      className="p-1 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload size={32} className="mx-auto text-gray-500 mb-2" />
                    <p className="text-gray-400">
                      {language === 'tr' ? 'Dosya seçmek için tıklayın' : 'Click to select file'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, PPT, PPTX</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={() => isEditing ? onUpdate(document.id) : onCreate(category)}
              disabled={uploading}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #00F0FF 0%, #06B6D4 100%)', color: '#0F172A' }}
            >
              {uploading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Save size={18} />
              )}
              {language === 'tr' ? 'Kaydet' : 'Save'}
            </button>
            <button
              onClick={onCancel}
              className="px-6 py-3 rounded-xl font-medium text-gray-400 hover:text-white transition-colors"
            >
              {language === 'tr' ? 'İptal' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Empty State - Create Button */}
      {!document && !isCreatingThis && (
        <div className="text-center py-8">
          <div 
            className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4"
            style={{ background: 'rgba(148, 163, 184, 0.1)' }}
          >
            {icon}
          </div>
          <p className="text-gray-400 mb-6">
            {language === 'tr' ? 'Henüz döküman eklenmemiş' : 'No document added yet'}
          </p>
          <button
            onClick={() => onStartCreate(category)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all"
            style={{ background: 'linear-gradient(135deg, #00F0FF 0%, #06B6D4 100%)', color: '#0F172A' }}
          >
            <Upload size={18} />
            {language === 'tr' ? 'Döküman Ekle' : 'Add Document'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminCompetitionDocs;
